import { useState } from 'react';

function CodeBlock({ code, language = 'json5' }: { code: string; language?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative group my-4">
      <div className="flex items-center justify-between bg-slate-800 rounded-t-lg px-4 py-2 border border-slate-700 border-b-0">
        <span className="text-xs text-slate-400 font-mono">{language}</span>
        <button onClick={handleCopy} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-400 transition-colors cursor-pointer whitespace-nowrap">
          <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
          {copied ? '已复制' : '复制'}
        </button>
      </div>
      <pre className="bg-slate-950 border border-slate-700 rounded-b-lg p-4 overflow-x-auto">
        <code className="text-sm text-slate-300 font-mono leading-relaxed">{code}</code>
      </pre>
    </div>
  );
}

function InfoBox({ type = 'info', children }: { type?: 'info' | 'warning' | 'tip'; children: React.ReactNode }) {
  const styles = {
    info: { bg: 'bg-cyan-500/5 border-cyan-500/20', icon: 'ri-information-line', iconColor: 'text-cyan-400' },
    warning: { bg: 'bg-amber-500/5 border-amber-500/20', icon: 'ri-alert-line', iconColor: 'text-amber-400' },
    tip: { bg: 'bg-emerald-500/5 border-emerald-500/20', icon: 'ri-lightbulb-line', iconColor: 'text-emerald-400' },
  };
  const s = styles[type];
  return (
    <div className={`${s.bg} border rounded-lg p-4 my-4`}>
      <div className="flex items-start gap-3">
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
          <i className={`${s.icon} ${s.iconColor} text-lg`}></i>
        </div>
        <div className="text-sm text-slate-300 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function SectionTitle({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2 id={id} className="text-xl font-bold text-white mt-10 mb-4 flex items-center gap-2 scroll-mt-24">
      <span className="w-1 h-5 bg-cyan-400 rounded-full inline-block"></span>
      {children}
    </h2>
  );
}

function SubTitle({ id, children }: { id?: string; children: React.ReactNode }) {
  return (
    <h3 id={id} className="text-base font-semibold text-slate-200 mt-6 mb-3 scroll-mt-24">{children}</h3>
  );
}

export default function SlashCommandsContent() {
  const commands = [
    { cmd: '/help', desc: '显示所有可用斜杠命令列表', args: '' },
    { cmd: '/model', desc: '切换当前会话使用的 AI 模型', args: '[model-id]' },
    { cmd: '/debug', desc: '切换调试模式，显示详细日志', args: '[on|off]' },
    { cmd: '/config', desc: '查看或更新运行时配置项', args: '[key] [value]' },
    { cmd: '/clear', desc: '清空当前会话的对话历史', args: '' },
    { cmd: '/compact', desc: '手动触发会话压缩', args: '' },
    { cmd: '/memory', desc: '查看或编辑智能体记忆文件', args: '[show|edit|clear]' },
    { cmd: '/skills', desc: '列出当前已加载的 Skills', args: '' },
    { cmd: '/subagents', desc: '列出当前运行中的子智能体', args: '' },
    { cmd: '/sessions', desc: '列出所有活跃会话', args: '' },
    { cmd: '/workspace', desc: '显示当前工作区路径', args: '' },
    { cmd: '/version', desc: '显示 OpenClaw 版本信息', args: '' },
    { cmd: '/reload', desc: '重新加载配置文件（热重载）', args: '' },
    { cmd: '/stop', desc: '停止当前正在执行的任务', args: '' },
    { cmd: '/approve', desc: '批准待审批的操作', args: '[id]' },
    { cmd: '/reject', desc: '拒绝待审批的操作', args: '[id]' },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-3">斜杠命令</h1>
        <p className="text-slate-400 text-sm leading-relaxed">
          斜杠命令（Slash Commands）是用户在聊天界面中直接控制 OpenClaw 行为的快捷指令。
          以 <code className="text-cyan-400 bg-slate-800 px-1.5 py-0.5 rounded text-xs">/</code> 开头，
          区别于普通的对话消息。
        </p>
      </div>

      <SectionTitle id="sc-diff">命令 vs 指令的区别</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
        <div className="bg-slate-800/40 border border-cyan-500/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <i className="ri-terminal-line text-cyan-400"></i>
            <span className="text-sm font-semibold text-white">斜杠命令</span>
          </div>
          <p className="text-sm text-slate-300 mb-3">以 <code className="text-cyan-400 bg-slate-700 px-1 rounded text-xs">/</code> 开头，直接控制 OpenClaw 系统行为，不经过 AI 处理。</p>
          <div className="space-y-1.5">
            <code className="block text-xs text-cyan-400 font-mono bg-slate-900 px-2 py-1 rounded">/model claude-3-5-sonnet</code>
            <code className="block text-xs text-cyan-400 font-mono bg-slate-900 px-2 py-1 rounded">/debug on</code>
            <code className="block text-xs text-cyan-400 font-mono bg-slate-900 px-2 py-1 rounded">/clear</code>
          </div>
        </div>
        <div className="bg-slate-800/40 border border-amber-500/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <i className="ri-chat-3-line text-amber-400"></i>
            <span className="text-sm font-semibold text-white">普通指令</span>
          </div>
          <p className="text-sm text-slate-300 mb-3">普通文本消息，由 AI 智能体处理，AI 会理解意图并执行相应操作。</p>
          <div className="space-y-1.5">
            <code className="block text-xs text-amber-400 font-mono bg-slate-900 px-2 py-1 rounded">帮我搜索最新的 Python 教程</code>
            <code className="block text-xs text-amber-400 font-mono bg-slate-900 px-2 py-1 rounded">创建一个 hello.py 文件</code>
            <code className="block text-xs text-amber-400 font-mono bg-slate-900 px-2 py-1 rounded">总结一下我们的对话</code>
          </div>
        </div>
      </div>
      <InfoBox type="info">
        斜杠命令由 OpenClaw 系统直接处理，响应速度更快，不消耗 AI token。
        适合用于系统控制操作，如切换模型、清空历史等。
      </InfoBox>

      <SectionTitle id="sc-config">配置斜杠命令</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        在 <code className="text-cyan-400 bg-slate-800 px-1.5 py-0.5 rounded text-xs">openclaw.json</code> 中通过
        <code className="text-cyan-400 bg-slate-800 px-1.5 py-0.5 rounded text-xs ml-1">commands.*</code> 配置斜杠命令的行为：
      </p>
      <CodeBlock language="json5" code={`{
  commands: {
    // 是否启用斜杠命令（默认 true）
    enabled: true,
    // 命令前缀（默认 "/"）
    prefix: "/",
    // 禁用特定命令
    disabled: ["debug", "config"],
    // 自定义命令别名
    aliases: {
      "/m": "/model",
      "/c": "/clear",
    },
    // 是否在聊天界面显示命令提示
    showSuggestions: true,
  }
}`} />

      <SectionTitle id="sc-list">完整命令列表</SectionTitle>
      <div className="overflow-x-auto my-4">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-slate-700">
              <th className="text-left py-2.5 px-3 text-slate-400 font-medium w-1/4">命令</th>
              <th className="text-left py-2.5 px-3 text-slate-400 font-medium w-1/4">参数</th>
              <th className="text-left py-2.5 px-3 text-slate-400 font-medium">说明</th>
            </tr>
          </thead>
          <tbody>
            {commands.map((cmd, i) => (
              <tr key={i} className="border-b border-slate-800/60 hover:bg-slate-800/30 transition-colors">
                <td className="py-2.5 px-3">
                  <code className="text-cyan-400 font-mono text-xs">{cmd.cmd}</code>
                </td>
                <td className="py-2.5 px-3">
                  {cmd.args ? (
                    <code className="text-amber-400 font-mono text-xs">{cmd.args}</code>
                  ) : (
                    <span className="text-slate-600 text-xs">—</span>
                  )}
                </td>
                <td className="py-2.5 px-3 text-slate-300">{cmd.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SectionTitle id="sc-model">模型选择 /model</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        使用 <code className="text-cyan-400 bg-slate-800 px-1.5 py-0.5 rounded text-xs">/model</code> 命令可以在会话中动态切换 AI 模型，
        无需重启 OpenClaw：
      </p>
      <CodeBlock language="bash" code={`# 列出所有可用模型
/model

# 切换到指定模型
/model claude-3-5-sonnet-20241022
/model gpt-4o
/model gemini-1.5-pro

# 切换到 OpenRouter 模型
/model openrouter/anthropic/claude-3.5-sonnet

# 切换回默认模型
/model default`} />
      <InfoBox type="tip">
        模型切换仅对当前会话生效，不会修改配置文件。重启后会恢复到配置文件中指定的默认模型。
      </InfoBox>

      <SectionTitle id="sc-debug">调试覆盖 /debug</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        <code className="text-cyan-400 bg-slate-800 px-1.5 py-0.5 rounded text-xs">/debug</code> 命令可以临时开启调试模式，
        显示详细的工具调用日志、模型请求/响应等信息：
      </p>
      <CodeBlock language="bash" code={`# 开启调试模式
/debug on

# 关闭调试模式
/debug off

# 查看当前调试状态
/debug

# 开启特定模块的调试
/debug tools     # 工具调用日志
/debug model     # 模型请求/响应
/debug session   # 会话状态变化`} />

      <SectionTitle id="sc-config-cmd">配置更新 /config</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        使用 <code className="text-cyan-400 bg-slate-800 px-1.5 py-0.5 rounded text-xs">/config</code> 命令可以在运行时查看和临时修改配置项：
      </p>
      <CodeBlock language="bash" code={`# 查看所有配置
/config

# 查看特定配置项
/config agents.0.model

# 临时修改配置（仅当前运行时生效）
/config agents.0.maxTokens 4096
/config session.maxHistory 50

# 重置为默认值
/config agents.0.maxTokens reset`} />
      <InfoBox type="warning">
        通过 <code className="text-amber-300 bg-slate-800 px-1 py-0.5 rounded text-xs">/config</code> 修改的配置仅在当前运行时生效，
        重启后会恢复到配置文件中的值。如需永久修改，请直接编辑 <code className="text-amber-300 bg-slate-800 px-1 py-0.5 rounded text-xs">openclaw.json</code>。
      </InfoBox>

      <SectionTitle id="sc-platform">平台注意事项</SectionTitle>
      <SubTitle>Telegram</SubTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        Telegram 原生支持斜杠命令，会在输入框中显示命令提示。
        OpenClaw 会自动向 BotFather 注册命令列表，用户可以通过 Telegram 的命令菜单快速访问。
      </p>
      <SubTitle>Discord</SubTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        Discord 的斜杠命令（Slash Commands）是独立的 API，与 OpenClaw 的斜杠命令系统不同。
        OpenClaw 的斜杠命令在 Discord 中以普通消息形式发送，以 <code className="text-cyan-400 bg-slate-800 px-1 py-0.5 rounded text-xs">/</code> 开头即可触发。
      </p>
      <SubTitle>WhatsApp / Signal</SubTitle>
      <p className="text-slate-300 text-sm leading-relaxed">
        这些平台不支持原生斜杠命令，但 OpenClaw 会识别以 <code className="text-cyan-400 bg-slate-800 px-1 py-0.5 rounded text-xs">/</code> 开头的消息并作为命令处理。
        注意：某些平台可能会将 <code className="text-cyan-400 bg-slate-800 px-1 py-0.5 rounded text-xs">/</code> 解释为其他含义，如遇问题可在配置中修改命令前缀。
      </p>
    </div>
  );
}
